import { Component, OnInit, OnDestroy, Input, inject } from '@angular/core';
import { FormularioDialogService } from '../../../../auxiliares/servicios/formulario-dialog.service';
import {
  INota,
  IPopulate,
  IQueryParam,
  IFilter,
  TipoNota,
  AccionesRol,
} from 'modelos/src';
import { firstValueFrom, Subscription } from 'rxjs';
import { AuxiliaresModule } from '../../../../auxiliares/auxiliares.module';
import { DialogService } from '../../../../auxiliares/dialog/dialog.service';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { NotasService } from '../../../../auxiliares/servicios/http/notas.service';
import { IRegExpSearch } from '../../../../auxiliares/tabla/filtro/filtro.component';
import {
  IColumnas,
  ICrearBoton,
} from '../../../../auxiliares/tabla/tabla.component';
import {
  estadoHtml,
  fechaCelda,
} from '../../../../auxiliares/tabla/celdas';
import { IEstadoCelda } from '../../../../auxiliares/tabla/tabla.interfaces';
import { CrearEditarNotaComponent } from '../crear-editar-nota/crear-editar-nota.component';
import { DetallesNotaComponent } from '../detalles-nota/detalles-nota.component';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';
import { ClienteJerarquiaService } from '../../../../auxiliares/servicios/cliente-jerarquia.service';
import { escaparHtml } from '../../../../auxiliares/html-seguro.util';

@Component({
  selector: 'app-listado-notas',
  templateUrl: './listado-notas.component.html',
  styleUrl: './listado-notas.component.scss',
  imports: [AuxiliaresModule],
})
export class ListadoNotasComponent implements OnInit, OnDestroy {
  private clienteJerarquia = inject(ClienteJerarquiaService);
  private lista2 = inject(Lista2Service);
  private helper = inject(HelperService);
  private formularios = inject(FormularioDialogService);
  private service = inject(NotasService);
  private dialogService = inject(DialogService);

  //// Tabla nueva
  @Input() public idAsignado?: string;
  // Cliente de la entidad asignada. Si no se pasa, el backend crea la nota con
  // el cliente del permiso (el del usuario logueado), que puede no ser el mismo.
  @Input() public idCliente?: string;
  @Input() public tipo?: TipoNota;
  @Input() public tipoDispositivo?:
    | 'Activo'
    | 'Vehiculo'
    | 'Colectivo'
    | 'Luminaria'
    | 'Alarma';
  public name = 'ListadoNotasComponent';
  public notas?: INota[];
  public totalCount = 0;
  public columnas?: IColumnas<INota>[];
  public botonCrear: ICrearBoton;
  public search: IRegExpSearch = {
    fields: ['tipo'],
  };

  //Subs
  public notas$?: Subscription;
  //
  private childrenLevel?: number = -1;
  private includeChildren?: boolean;
  //

  // FILTROS

  public populate: IPopulate[] = [
    {
      path: 'cliente',
      select: 'nombre',
    },
    {
      path: 'alarma',
    },
    {
      path: 'luminaria',
    },
    {
      path: 'activo',
    },
    {
      path: 'categoriaEvento',
      select: 'nombre color',
    },
  ];
  public query: IQueryParam = {
    populate: JSON.stringify(this.populate),
    sort: 'orden',
    page: 0,
    limit: +this.helper.getPageSize(this.name),
    includeChildren: true,
    childrenLevel: 0,
  };

  private accionNotaOContacto(): AccionesRol | undefined {
    const esContacto = this.tipo === 'Contacto';
    switch (this.tipoDispositivo) {
      case 'Activo':
        return esContacto
          ? 'Activos - Editar contactos '
          : 'Activos - Editar notas';
      case 'Vehiculo':
        return esContacto
          ? 'Vehículos - Editar contactos '
          : 'Vehículos - Editar notas';
      case 'Colectivo':
        return esContacto
          ? 'Colectivos - Editar contactos '
          : 'Colectivos - Editar notas';
      case 'Luminaria':
        return esContacto
          ? 'Luminarias - Editar contactos '
          : 'Luminarias - Editar notas';
      case 'Alarma':
        return esContacto
          ? 'Alarmas - Editar contactos '
          : 'Alarmas - Editar notas';
      default:
        return undefined; // Ej. notas de Cliente: no hay AccionRol equivalente todavía
    }
  }

  private tienePermisoNotaOContacto(): boolean {
    const accion = this.accionNotaOContacto();
    return accion ? this.helper.tienePermiso(accion) : false;
  }

  private puedeMostrarBotonCrear(): boolean {
    return this.tienePermisoNotaOContacto();
  }

  // Listars
  public async listar(): Promise<void> {
    let filter: IFilter<INota>;
    if (this.query.filter) {
      filter = JSON.parse(this.query.filter);
    } else {
      filter = {};
    }
    filter.idAsignado = this.idAsignado;
    filter.tipo = this.tipo;
    this.query.filter = JSON.stringify(filter);
    this.notas$?.unsubscribe();
    this.notas$ = this.lista2.notas(this.query, 30).subscribe((data) => {
      this.totalCount = data.totalCount;
      this.notas = data.datos;
    });
  }
  // Acciones

  public async crear() {
    const dialogRef = this.formularios.abrir(CrearEditarNotaComponent, {
      tamano: 'mediano',
      data: {
        idEntidad: this.idAsignado,
        idCliente: this.idCliente,
        tipoDispositivo: this.tipoDispositivo,
        tipo: this.tipo,
      },
    });
    const res = await firstValueFrom(dialogRef.afterClosed());
    if (res) {
      this.listar();
    }
  }

  public async editar(nota: INota) {
    const dialogRef = this.formularios.abrir(CrearEditarNotaComponent, {
      tamano: 'mediano',
      data: {
        nota: nota,
        idEntidad: this.idAsignado,
        tipo: this.tipo,
        tipoDispositivo: this.tipoDispositivo,
      },
    });
    const res = await firstValueFrom(dialogRef.afterClosed());
    if (res) {
      this.listar();
    }
  }
  public async detalles(nota: INota) {
    this.formularios.abrir(DetallesNotaComponent, {
      tamano: 'chico',
      data: { ...nota.informacion, idNota: nota._id },
      cerrarAfuera: true,
    });
  }

  /** Vigencia de la nota según sus fechas: vigente, todavía no o vencida. */
  private estadoVigencia(desde: string | Date, hasta: string | Date): IEstadoCelda {
    const ahora = Date.now();
    if (desde && ahora < new Date(desde).getTime()) {
      return { texto: 'Todavía no vigente', tono: 'info', icono: 'pending' };
    }
    if (ahora > new Date(hasta).getTime()) {
      return { texto: 'Vencida', tono: 'error', icono: 'timer_off' };
    }
    return { texto: 'Vigente', tono: 'ok', icono: 'toggle_on' };
  }

  public async eliminar(nota: INota) {
    const res = await this.dialogService.confirm(
      'Eliminar nota',
      `¿Querés eliminar la nota?`,
    );
    if (!res) return;
    try {
      await this.service.delete(nota._id!);
      this.helper.notifSuccess('Nota eliminada');
      await this.listar();
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
  }

  // Tabla

  private setColumnas() {
    const columnas: IColumnas<INota>[] = [
      //Cliente
      {
        header: {
          label: 'Orden de prioridad',
        },
        row: {
          parse(dato) {
            return dato.orden;
          },
        },
      },
      // Permanente
      {
        header: {
          label: 'Permanente',
        },
        row: {
          // Es un dato de la nota: Sí/No como texto
          parse: (dato) => (dato.permanente ? 'Sí' : 'No'),
        },
      },
      // Vigencia
      {
        header: {
          label: 'Vigencia',
        },
        row: {
          // Estado de la vigencia (antes sólo color) y el período debajo
          html: (dato) => {
            if (dato.vigenciaHasta) {
              const desde = fechaCelda(dato.vigenciaDesde).slice(0, 10);
              const hasta = fechaCelda(dato.vigenciaHasta).slice(0, 10);
              return `<div class="tabla-celda-col"><span>${estadoHtml(
                this.estadoVigencia(dato.vigenciaDesde, dato.vigenciaHasta),
              )}</span><span class="tabla-secundario">${escaparHtml(desde)} – ${escaparHtml(hasta)}</span></div>`;
            }
            return '';
          },
          noData: 'Sin vigencia',
        },
      },
      // Categoría de evento
      {
        header: {
          label: 'Categoría de evento',
        },
        row: {
          // Sin categoría la nota aplica a todos los eventos: es un valor real
          html(dato) {
            if (dato.categoriaEvento?.nombre) {
              // Badge del color de la categoría con el nombre adentro
              return estadoHtml({
                texto: dato.categoriaEvento.nombre,
                tono: 'neutro',
                color: dato.categoriaEvento.color,
              });
            }
            return '<span>Todos los eventos</span>';
          },
        },
      },
    ];

    // Partición: sólo las alarmas tienen particiones, aplica tanto a Contactos como a Notas
    if (this.tipoDispositivo === 'Alarma') {
      columnas.push({
        header: {
          label: 'Partición',
        },
        row: {
          // Sin partición aplica a todas: es un valor real, no falta de dato
          parse(dato) {
            return dato?.informacion?.particion?.toString() ?? 'Todas';
          },
        },
      });
    }

    // Cliente
    if (HelperService.getTipoCliente() !== 'Final') {
      {
        columnas.push({
          header: {
            label: 'Cliente',
          },
          row: {
            html: (dato) => this.clienteJerarquia.htmlNombre(dato.cliente),
            tooltip: (dato) =>
              this.clienteJerarquia.tooltipJerarquia(dato.cliente),
            noData: 'Sin cliente',
          },
        });
      }
    }
    // Acciones
    columnas.push({
      header: {
        label: 'Acciones',
      },
      row: {
        acciones: [
          {
            tipo: 'material',
            color: 'primary',
            icon: 'edit',
            tooltip: 'Editar',
            click: (dato) => this.editar(dato),
            disabled: () => !this.tienePermisoNotaOContacto(),
          },
          {
            tipo: 'material',
            color: 'primary',
            icon: 'visibility',
            tooltip: 'Ver detalle',
            click: (dato) => this.detalles(dato),
            disabled: () => !this.tienePermisoNotaOContacto(),
          },
          {
            tipo: 'material',
            color: 'warn',
            icon: 'delete',
            tooltip: 'Eliminar',
            click: (dato) => this.eliminar(dato),
            disabled: () => !this.tienePermisoNotaOContacto(),
          },
        ],
      },
    });

    this.columnas = columnas;
  }

  // Hooks

  async ngOnInit() {
    this.setColumnas();

    this.botonCrear = {
      mostrar: this.puedeMostrarBotonCrear(),
      tooltip: this.tipo === 'Contacto' ? 'Crear contacto' : 'Crear nota',
      color: 'primary',
      tipo: 'material',
      icon: 'add',
      accion: () => this.crear(),
    };

    if (this.tipo === 'Contacto') {
      const columnasContacto: IColumnas<INota>[] = [
        {
          header: {
            label: 'Contacto',
            sortable: true,
            sort: 'informacion.contacto',
          },
          row: {
            parse(dato) {
              return dato?.informacion?.contacto;
            },
            noData: 'Sin contacto',
          },
        },
      ];

      this.columnas = columnasContacto.concat(this.columnas);
    }
    if (!this.tipo) {
      const columnaTipo: IColumnas<INota>[] = [
        {
          header: {
            label: 'Tipo',
            sortable: true,
          },
          row: {
            field: 'tipo',
          },
        },
      ];
      this.columnas = columnaTipo.concat(this.columnas);
    }
    await this.listar();
  }

  async ngOnDestroy() {
    this.notas$?.unsubscribe();
  }
}
